import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import {
  matchPath,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { mapControlSurfaceToStatus } from './monitoring-control-surface-adapter';
import { buildControlEntries, buildSections } from './monitoring-sections';
import type { MonitoringSection } from './monitoring-sections';

import ProgressWithText from '~/client/components/progress-with-text';
import UserNavigationMenu from '~/client/components/settings/user-navigation-menu';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';
import { useGetGuardianControlSurfaceQuery } from '~/graphql/generated/client';
import Arranger from '~c/components/base/arranger';
import {
  LeftNavMenu,
  LeftNavMenuItem,
  useLeftNavContext,
} from '~c/components/base/left-nav';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import { useDashboardDataContext } from '~c/components/project/dashboard/dashboard-context';
import {
  aiSystemMonitoringRouteUtil,
  functionalModelMonitoringRouteUtil,
  useDashboardHandlers,
} from '~c/routes';

/** Query-param key the page and this nav share to agree on the open section. */
export const SECTION_PARAM = 'section';

interface Props {
  className?: string;
}

const MonitoringLeftNavImpl: React.FC<Props> = ({ className }) => {
  const { projectId, assessmentId } = useParams();
  const { pathname } = useLocation();
  // The sidebar shows only under the exact prefix the left nav matched, and a
  // monitoring policy opens under either the AI system or a use case — so it
  // answers to whichever of the two this page is on.
  const navName = matchPath(
    `${functionalModelMonitoringRouteUtil.routePrefix}/*`,
    pathname
  )
    ? functionalModelMonitoringRouteUtil.routePrefix
    : aiSystemMonitoringRouteUtil.routePrefix;
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLeftNavContext();
  const { navigateToMainDashboard } = useDashboardHandlers();
  const { project } = useDashboardDataContext();
  const t = useDesignTokens();

  const assessment = React.useMemo(
    () => project?.assessments?.find((a) => a._id === assessmentId) ?? null,
    [project?.assessments, assessmentId]
  );
  const policy = assessment?.policy ?? null;

  /*
   * The SAME query the page runs, with the same variables. Apollo serves it
   * from cache, so this is not a second network round-trip — it is the pattern
   * AssessmentLeftNav already uses (`useAssessmentFromId` beside the form's own
   * read). The alternative, lifting a provider above both the app shell and the
   * route outlet, would put monitoring state in the root for one screen.
   */
  const { data, loading } = useGetGuardianControlSurfaceQuery({
    variables: {
      projectId: projectId ?? '',
      policyId: policy?.identifier ?? '',
      assessmentId: assessmentId ?? '',
    },
    skip: !projectId || !policy?.identifier || !assessmentId,
    fetchPolicy: 'cache-first',
  });

  const sections: MonitoringSection[] = React.useMemo(() => {
    const snapshot = data?.guardianControlSurface
      ? mapControlSurfaceToStatus(data.guardianControlSurface)
      : null;
    return buildSections(buildControlEntries(policy, snapshot));
  }, [policy, data]);

  const activeSectionId =
    searchParams.get(SECTION_PARAM) ?? sections[0]?.id ?? '';

  const selectSection = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(SECTION_PARAM, id);
    // The section lives in the URL so the sidebar and the page agree without a
    // shared provider, and so a section is linkable. `replace` keeps Back
    // meaning "leave monitoring" rather than "undo my last eleven sections".
    setSearchParams(next, { replace: true });
  };

  const totalControls = sections.reduce((n, s) => n + s.total, 0);
  const totalCovered = sections.reduce((n, s) => n + s.covered, 0);
  const coveragePercent =
    totalControls > 0 ? (totalCovered / totalControls) * 100 : 0;

  return (
    <LeftNavMenu
      className={className}
      name={navName}
      header={{
        showBackButton: true,
        loading,
        onClick: () =>
          navigateToMainDashboard(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            project?.projectType
          ),
      }}
    >
      <div className="monitoring-status">
        <Text sz="body16" color="white" noWrap sx={{ mb: spc.xxs }}>
          {policy?.name ?? 'Runtime Monitoring'}
        </Text>
        <ProgressWithText
          value={coveragePercent}
          hideProgressBar={!state.open}
          inNav
        />
      </div>
      <div style={{ paddingBottom: '16px' }}>
        <div className="divider" />
      </div>

      {sections.map((s) => {
        const active = s.id === activeSectionId;
        return (
          <LeftNavMenuItem
            key={s.id}
            selected={active}
            onClick={() => selectSection(s.id)}
            data-testid={`monitoring-nav-section-${s.id}`}
          >
            <Arranger className="section-row">
              {/*
                One dot, highest attention only. A per-section count of watch
                and elevated would compete with the fraction beside it for the
                same glance; the rail routes the reader, the control reports.
              */}
              <span
                aria-hidden
                className={`attention-dot attention-${s.attention}`}
              />
              <Text className="section-name" noWrap component="span">
                {s.name}
              </Text>
              <Text className="section-count">
                {s.covered}/{s.total}
              </Text>
            </Arranger>
          </LeftNavMenuItem>
        );
      })}

      <div className="divider" />
      <Arranger
        className="total-row"
        alignContent="center"
        justify="space-between"
        sx={{ padding: 5, flexWrap: 'wrap' }}
      >
        {/*
          "Consulted", not "Total": the fraction counts controls consulted at
          least once, and a consulted control can still be sitting at
          'elevated'. The label carries that — do not re-add a sentence
          underneath explaining it.
        */}
        <Text sx={{ color: t.nav.itemTextOnDark }}>Consulted</Text>
        <Text sx={{ color: t.nav.itemTextOnDark }}>
          {totalCovered}/{totalControls}
        </Text>
      </Arranger>
      <Spacer className="grow" />
      <UserNavigationMenu />
    </LeftNavMenu>
  );
};

export const MonitoringLeftNav = muiStyled(MonitoringLeftNavImpl)(({
  theme,
}) => {
  return css`
    .grow {
      flex-grow: 1;
    }

    .monitoring-status {
      padding: 0px 16px;
      margin-top: ${spc.xxs}px;
      margin-bottom: ${spc.xxs}px;
      color: ${theme.palette.text.secondary};
    }

    .divider {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      padding: 0px 16px;
      width: 260px;
      height: 1px;
      background-color: ${theme.palette.divider};
      align-self: stretch;
    }

    .section-row {
      display: flex;
      align-items: center;
      width: 100%;
      min-width: 0;
      gap: 8px;
    }

    .attention-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex: 0 0 auto;
      background: transparent;
    }
    .attention-watch {
      background: ${theme.palette.warning.main};
    }
    .attention-elevated {
      background: ${theme.palette.error.main};
    }

    .section-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1 1 auto;
      min-width: 0;
    }

    .section-count {
      flex: 0 0 auto;
      white-space: nowrap;
      font-size: 14px;
    }
  `;
});

export default MonitoringLeftNav;
